import { useRouter } from 'next/router';
import { isAiConfigAvailableForUser } from 'shared-helpers/aiTools';
import { useAchievementTypes, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { PageSideNav } from 'components/page';

import type { GetPendingIndividualAchievementsRes } from 'shared-types/responses';

interface Props {
  categories?: {
    id: number;
    title: string;
  }[];
}

const AchievementsNav = ({ categories }: Props) => {
  const { messages, serverConfig, user } = useReduxState();

  const discussionAnalysisAvailable = isAiConfigAvailableForUser({
    requiredAiExtraServerConfig: 'aiExtraDiscussionAnalysis',
    serverConfig,
    user
  });

  const router = useRouter();

  const achievementTypes = useAchievementTypes();

  const { data } = useApiData<GetPendingIndividualAchievementsRes>({
    url:
      '/admin/achievements-individual/access-request' +
      '?onlyFetchNumberOfRequests=true',
    onlyIf: Boolean(
      serverConfig.individualAchievementsEnabled &&
        (user?.roles.includes('supervisor') ||
          user?.roles.includes('superadmin'))
    )
  });

  const type = router.query.type as string;
  const achievementType = router.query.achievementType as string | string[];
  const categoryId = router.query.categoryId as string | string[];
  const presentInAll = router.query.presentInAll as string | string[];

  let preservedFilters = '';
  if (typeof achievementType === 'string') {
    preservedFilters += `&achievementType=${achievementType}`;
  } else if (typeof achievementType === 'object') {
    preservedFilters += achievementType
      .map((value) => `&achievementType=${value}`)
      .join('');
  }

  if (typeof categoryId === 'string') {
    preservedFilters += `&categoryId=${categoryId}`;
  } else if (typeof categoryId === 'object') {
    preservedFilters += categoryId.map((id) => `&categoryId=${id}`).join('');
  }

  if (typeof presentInAll === 'string') {
    preservedFilters += `&presentInAll=${presentInAll}`;
  } else if (typeof presentInAll === 'object') {
    preservedFilters += presentInAll
      .map((value) => `&presentInAll=${value}`)
      .join('');
  }

  const categoryFilters =
    categories?.length && !serverConfig.useAlternativeCategoryFilter
      ? {
          [messages.commonCategories]: [
            ...categories.map((category) => ({
              label: category.title,
              value: `&categoryId=${category.id}`
            }))
          ]
        }
      : {};

  const filters = {
    ...(['active', 'search'].includes(type)
      ? {
          [messages.commonStatus]: [
            {
              label: messages.commonCompleted,
              value: '&completionStatus=yes'
            },
            {
              label: messages.commonValid,
              value: '&completionStatus=valid'
            },
            {
              label: messages.commonExpiring,
              value: '&completionStatus=expiring'
            },
            {
              label: messages.commonExpired,
              value: '&completionStatus=expired'
            },
            {
              label: messages.commonInProgress,
              value: '&completionStatus=inprogress'
            },
            {
              label: messages.commonNoCompletion,
              value: '&completionStatus=no'
            }
          ]
        }
      : {}),
    ...categoryFilters,
    [messages.commonType]: Object.keys(achievementTypes).map(
      (achievementType) => ({
        label: achievementTypes[achievementType].title,
        value: `&achievementType=${achievementType}`
      })
    )
  };

  const filtersSeparate = {
    ...(categories?.length &&
      serverConfig.useAlternativeCategoryFilter && {
        [messages.commonCategories]: [
          ...categories.map((category) => ({
            label: category.title,
            value: `&categoryId=${category.id}`
          }))
        ]
      })
  };

  const sections = [
    {
      links: [
        ...(user?.roles.includes('superadmin')
          ? [
              {
                href: '/achievements?type=all' + preservedFilters,
                icon: 'list' as const,
                isActive: type === 'all',
                text: messages.commonAllAchievements
              }
            ]
          : []),
        ...(user?.roles.includes('admin')
          ? [
              {
                href: '/achievements?type=administered' + preservedFilters,
                icon: 'edit' as const,
                isActive: type === 'administered',
                text: messages.coursesAdministered
              }
            ]
          : []),
        {
          href: '/achievements?type=active' + preservedFilters,
          icon: 'check-circle' as const,
          isActive: type === 'active',
          text: messages.achievementsMyAchievements
        },
        {
          href: '/achievements?type=available' + preservedFilters,
          icon: 'book-open' as const,
          isActive: type === 'available',
          text: messages.commonAvailable
        },
        ...(serverConfig.individualAchievementsEnabled
          ? [
              {
                href: '/achievements/individual',
                icon: 'star' as const,
                isActive: router.pathname?.startsWith(
                  '/achievements/individual'
                ),
                text: messages.individualAchievements,
                unreadItems: data?.numberOfRequests
              }
            ]
          : []),
        ...(discussionAnalysisAvailable
          ? [
              {
                href: '/achievements/conversation-analyses',
                icon: 'wand-magic-sparkles' as const,
                isActive: router.pathname?.startsWith(
                  '/achievements/conversation-analyses'
                ),
                text: messages.achievementConversationAnalyses
              }
            ]
          : [])
      ]
    }
  ];

  return (
    <div className="nav">
      <PageSideNav
        disableFilters={!type}
        filters={filters}
        filtersSeparate={filtersSeparate}
        filtersWithRequireAll={['categoryId']}
        linkSections={sections}
        shallowNavigation={true}
      />

      <style jsx>{`
        .nav {
          display: flex;
          margin-bottom: 20px;
        }
      `}</style>
    </div>
  );
};

export default AchievementsNav;
